import {useFormatCurrency, useFormatNumber} from 'platform/locale';

import {isNumber} from 'ramda-adjunct';

import {selectRows, selectSelectedCellRange} from '../../store/createEagGridStore';
import {useEagGridSelector} from '../../store/useEagGridSelector';
import type {EagColumn} from '../../types/EagColumn';
import {getValueFromRawCell} from '../../utils/getValueFromRawCell';

type CellValue = {amount: number; currency: string | null | undefined};

const DECIMAL_PLACES = 2;
const STATUS_BAR_BORDER = {borderTop: '1px solid #E2E8F0'} as const;
const STATUS_BAR_TEXT_COLOR = {color: 'rgba(24, 29, 31, 0.5)'} as const;
const STATUS_BAR_STYLE = {...STATUS_BAR_BORDER, ...STATUS_BAR_TEXT_COLOR} as const;

type AggregationStatusBarProps = {
  visibleColumns: EagColumn[];
};

/** Gate component: only mounts the heavy body when a range is selected. */
export function AggregationStatusBar(props: AggregationStatusBarProps) {
  const range = useEagGridSelector(selectSelectedCellRange);

  if (!range || range.columnKeys.length === 0) {
    return null;
  }

  return <AggregationStatusBarBody visibleColumns={props.visibleColumns} range={range} />;
}

type AggregationStatusBarBodyProps = {
  visibleColumns: EagColumn[];
  range: {columnKeys: string[]; minRow: number; maxRow: number};
};

/** Subscribes to rows only when a selection is active. */
function AggregationStatusBarBody(props: AggregationStatusBarBodyProps) {
  const {range} = props;
  const rows = useEagGridSelector(selectRows);
  const formatCurrency = useFormatCurrency();
  const formatNumber = useFormatNumber();

  // Build a lookup of selected columns
  const selectedColumns = range.columnKeys
    .map((key) => props.visibleColumns.find((c) => c.key === key))
    .filter(Boolean) as EagColumn[];

  if (selectedColumns.length === 0) {
    return null;
  }

  // Extract numeric values from selected cells within the row range
  const cellValues: CellValue[] = [];
  for (let rowIdx = range.minRow; rowIdx <= range.maxRow; rowIdx++) {
    const row = rows[rowIdx];
    if (!row) {
      continue;
    }
    for (const column of selectedColumns) {
      const rawValue = row[column.field];
      const value = getValueFromRawCell(column, rawValue);
      if (value == null) {
        continue;
      }
      if (isNumber(value)) {
        cellValues.push({amount: value, currency: null});
      } else if (typeof value === 'object' && 'amount' in value && isNumber(value.amount)) {
        cellValues.push({
          amount: value.amount,
          currency: (value as {amount: number; currency?: string}).currency ?? null,
        });
      }
    }
  }

  if (cellValues.length === 0) {
    return null;
  }

  const amounts = cellValues.map((c) => c.amount);
  const count = amounts.length;
  const sumValue = amounts.reduce((a, b) => a + b, 0);
  const avgValue = sumValue / count;
  const minValue = amounts.reduce((a, b) => Math.min(a, b), Infinity);
  const maxValue = amounts.reduce((a, b) => Math.max(a, b), -Infinity);

  // If all cells share the same currency, format with currency; otherwise plain number
  const currencies = cellValues.map((c) => c.currency).filter(Boolean);
  const sharedCurrency =
    currencies.length === cellValues.length && currencies.every((c) => c === currencies[0])
      ? (currencies[0] as string)
      : null;

  const fmt = (value: number): string =>
    (sharedCurrency
      ? formatCurrency(value, sharedCurrency, DECIMAL_PLACES)
      : formatNumber(value, DECIMAL_PLACES)) ?? String(value);

  return (
    <div
      className="flex items-center justify-end gap-2 px-6 py-1 text-xs"
      style={STATUS_BAR_STYLE}
      data-testid="aggregation-status-bar"
    >
      <StatusItem label="Count" value={String(count)} />
      <StatusItem label="Average" value={fmt(avgValue)} />
      <StatusItem label="Min" value={fmt(minValue)} />
      <StatusItem label="Max" value={fmt(maxValue)} />
      <StatusItem label="Sum" value={fmt(sumValue)} />
    </div>
  );
}

function StatusItem(props: {label: string; value: string}) {
  return (
    <span className="ml-2">
      <span>{props.label}:</span>{' '}
      <span className="font-bold" style={{color: '#181d1f'}}>
        {props.value}
      </span>
    </span>
  );
}
